import { IFileSizeUnitEnum } from "@interfaces";

import type { ParseKeys, TFunction } from "i18next";

import type { IFileObject, IFileResponse, IFileSize } from "@interfaces/common";

export const unitTranslationMapper: Record<
  IFileSizeUnitEnum,
  ParseKeys<"component">
> = {
  Bytes: "file.sizeUnits.Bytes",
  Gb: "file.sizeUnits.Gb",
  Kb: "file.sizeUnits.Kb",
  Mb: "file.sizeUnits.Mb",
  Tb: "file.sizeUnits.Tb",
};

/**
 * Formats a file size in bytes into a human-readable string, using either SI (base 10) or IEC (base 2) units.
 *
 * @param size The file size in bytes.
 * @param base10 (Optional) Whether to use SI units (base 10) for conversion. Defaults to `true` (IEC units, base 2).
 * @returns A human-readable string representing the file size, with the appropriate unit (Bytes, KB, MB, GB, etc.).
 *
 */
export function humanizedFileSize(
  size: number,
  base10 = true,
): {
  size: number;
  unit: IFileSizeUnitEnum;
} {
  if (Number.isNaN(size) || !Number.isFinite(size)) {
    // Handle invalid numeric inputs (NaN, Infinity, -Infinity)
    return {
      size: Number.NaN,
      unit: IFileSizeUnitEnum.BYTES,
    };
  }
  if (size === 0)
    return {
      size: 0,
      unit: IFileSizeUnitEnum.BYTES,
    };

  // eslint-disable-next-line id-length
  const k = base10 ? 1000 : 1024;
  const dm = 2;
  //   const units = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const units = [
    IFileSizeUnitEnum.BYTES,
    IFileSizeUnitEnum.KB,
    IFileSizeUnitEnum.MB,
    IFileSizeUnitEnum.GB,
    IFileSizeUnitEnum.TB,
  ];
  const absSize = Math.abs(size);
  const i = Math.floor(Math.log(absSize) / Math.log(k));

  const result = Number.parseFloat((absSize / k ** i).toFixed(dm));

  return {
    size: result,
    unit: units[i] || IFileSizeUnitEnum.BYTES,
  };
}

/**
 * Parses a human-readable file size string and attempts to extract the numeric value in bytes.
 *
 * @param fileSizeString The human-readable file size string (e.g., "123.45 KB").
 * @param base10 (Optional) Whether to use SI units (base 10) for conversion. Defaults to `true` (IEC units, base 2).
 * @returns The extracted numeric value in bytes, or NaN if parsing fails.
 */
export function parseHumanizedFileSize(
  fileSize: IFileSize | number,
  base10 = true,
): number {
  if (typeof fileSize === "number") {
    return fileSize;
  }

  // // Case-insensitive regex
  // // eslint-disable-next-line unicorn/better-regex
  // const match = fileSizeString.match(/^([-\d.]+)\s*(?:([A-Z]+)?)?/i);

  // if (!match?.[1]) {
  //   // No match for valid format
  //   return Number.NaN;
  // }

  // const value = Number.parseFloat(match[1]);
  // // Default to Bytes
  // const unit = match[2]?.toUpperCase() || IFileSizeUnit.BYTES;

  // eslint-disable-next-line id-length
  const k = base10 ? 1000 : 1024;

  const multipliers = {
    [IFileSizeUnitEnum.BYTES]: 1,
    [IFileSizeUnitEnum.KB]: k,
    [IFileSizeUnitEnum.MB]: k * k,
    [IFileSizeUnitEnum.GB]: k * k * k,
    [IFileSizeUnitEnum.TB]: k * k * k * k,
    // PB: k * k * k * k * k,
    // EB: k * k * k * k * k * k,
    // ZB: k * k * k * k * k * k * k,
    // YB: k * k * k * k * k * k * k * k,
  };

  // if (!Object.prototype.hasOwnProperty.call(multipliers, unit)) {
  //   // Invalid unit
  //   return Number.NaN;
  // }

  return fileSize.size * multipliers[fileSize.unit];
}

export function calcProgressPercentage(
  total: number | IFileSize | undefined,
  loaded: number | IFileSize | undefined,
) {
  if (typeof total === "number") {
    if (typeof loaded === "number") {
      return calcProgressPercentageForNumber(total, loaded);
    }
    return calcProgressPercentageForNumber(total, loaded?.size);
  }
  if (typeof loaded === "number") {
    return calcProgressPercentageForNumber(total?.size, loaded);
  }
  return calcProgressPercentageForNumber(total?.size, loaded?.size);
}

export function calcProgressPercentageForNumber(
  total: number | undefined,
  loaded: number | undefined,
) {
  if (
    total !== undefined &&
    loaded !== undefined &&
    !Number.isNaN(total) &&
    !Number.isNaN(loaded)
  ) {
    return Math.round((loaded * 100) / total);
  }
  return 0;
}

export function getFileSizeTranslation(
  t: TFunction<"component">,
  fileSize: IFileSize | number,
  maxSize?: IFileSize | number,
) {
  const maxSizeValue = parseHumanizedFileSize(maxSize || 0);
  const sizeValue = parseHumanizedFileSize(fileSize || 0);

  const totalSize = maxSize ? Math.min(maxSizeValue!, sizeValue) : sizeValue;

  const finalSize = humanizedFileSize(totalSize);

  return `${finalSize.size} ${t(unitTranslationMapper[finalSize.unit])}`;
}

export const fileResponseMapper = (
  file: IFileResponse | undefined,
): IFileObject => {
  if (file) {
    return {
      size: file.size_bytes,
      type: file.mime_type,
      name: file.name,
      url: file.url,
      id: file.id,
    };
  }
};

export function blobToFile(blob: Blob, fileName: string): File {
  // Create a new File object from the Blob
  return new File([blob], fileName, {
    type: blob.type,
    lastModified: Date.now(),
  });
}

export function convertFileToFileObject(file: File): IFileObject {
  return {
    size: file.size,
    url: globalThis.URL.createObjectURL(file),
    name: file.name,
    type: file.type,
    id: crypto.randomUUID(),
  };
}
